<script setup>
//#region import
import { ref, computed, onBeforeMount, watch } from 'vue';
import { isEmpty } from 'lodash-es';
import hash from 'object-hash';
import { useFilterUtils } from '@/composables/useFilterUtils';
import { usePermissions } from '@/composables/usePermissions';
//#endregion

//#region define
const props = defineProps({
  filtersList: {
    type: Array,
    default: () => [],
  },
  canBeUndefined: {
    type: Array,
    default: () => null,
  },
  modelValue: {
    type: Object,
    default: () => {},
  },
});

const emit = defineEmits(['submit', 'notApplyState', 'open']);
//#endregion

//#region const
const componentMap = {
  radio: {
    component: 'UiRadioGroup',
  },
  select: {
    component: 'UiSelectElement',
    props: {
      width: '200',
      ellipsis: true,
      filterable: true,
      clearable: true,
    },
  },
};
//#endregion

//#region composables
const { apiToCustom, customToApi } = useFilterUtils({
  canBeUndefined: props.canBeUndefined,
});
const { hasPermission } = usePermissions();
//#endregion

//#region data
const currentFilters = ref({});
const oldHash = ref(null);
const newHash = ref(null);
//#endregion

//#region computed
const availableFilters = computed(() => {
  return props.filtersList.filter((filter) => !filter.permission || hasPermission(filter.permission));
});

const isActiveButton = computed(() => {
  return oldHash.value !== newHash.value;
});

const hasFilters = computed(() => {
  return !isEmpty(props.modelValue);
});
//#endregion

//#region watch
watch(currentFilters, (newVal) => {
  newHash.value = hash(newVal, { algorithm: 'md5' });
  emit('notApplyState', currentFilters.value);
}, { deep: true, immediate: true });

watch(() => availableFilters.value, (newFilters) => {
  if (!newFilters || !currentFilters.value) return;
  const allowedIds = new Set(newFilters.map((f) => String(f.id)));
  Object.keys(currentFilters.value).forEach((key) => {
    if (!allowedIds.has(key)) {
      if (currentFilters.value[key] !== undefined) {
        delete currentFilters.value[key];
      }
    }
  });
  newHash.value = hash(currentFilters.value, { algorithm: 'md5' });
}, { deep: true, immediate: true });
//#endregion

//#region methods
function handleApply() {
  const hasValue = Object.values(currentFilters.value).some((value) => {
    if (value === undefined || value === null) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'string') return value.trim() !== '' && value.trim() !== 'undefined';
    return true;
  });

  if (!hasValue) {
    currentFilters.value = {};
  }
  emit('submit', customToApi(currentFilters.value));
}

function handleReset() {
  currentFilters.value = {};
  emit('submit', currentFilters.value);
}

function getFilterParams() {
  const filteredModelValue = availableFilters.value.reduce((acc, availableFilterItem) => {
    const [filter] = props.filtersList.filter((filterListItem) => filterListItem.name === availableFilterItem.name);
    acc[filter.id] = props.modelValue[filter.id];
    return acc;
  }, {});

  currentFilters.value = filteredModelValue;
  const data = filteredModelValue;

  const currentFiltersToCustom = apiToCustom(currentFilters.value);
  const dataToCustom = apiToCustom(data);

  currentFilters.value = currentFiltersToCustom;
  oldHash.value = hash(dataToCustom, { algorithm: 'md5' });
  emit('open');
}
//#endregion

//#region hook
onBeforeMount(() => {
  getFilterParams();
});
//#endregion
</script>

<template>
  <UiFilterElement
    :is-active-button="isActiveButton"
    :has-filters="hasFilters"
    @open="getFilterParams"
    @apply="handleApply"
    @reset="handleReset"
  >
    <UiFilterItem
      v-for="filter in availableFilters"
      :key="filter.id"
      :label="$t(`ui.filters.${filter.name}`)"
    >
      <component
        :is="componentMap[filter.type].component"
        v-model="currentFilters[filter.id]"
        v-bind="{
          ...componentMap[filter.type].props,
          ...filter.params,
        }"
      />
    </UiFilterItem>
  </UiFilterElement>
</template>
